import Head from "next/head";
import { motion } from "framer-motion";
import { Button } from "@/components/Button";
import { useRouter } from "next/router";
import { useTranslations } from 'next-intl';
import { SEOMeta } from "@/templates/SEOMeta";
import {
  ChartBarIcon,
  AdjustmentsHorizontalIcon,
  LinkIcon,
  TicketIcon,
  CodeBracketIcon,
  Cog6ToothIcon,
  ClipboardDocumentCheckIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ShieldCheckIcon,
  BoltIcon,
  FunnelIcon,
  DocumentArrowDownIcon,
} from "@heroicons/react/24/outline";
import {SeoInternalLinks} from "@/components/SeoInternalLinks";
import { getI18nProps } from '@/utils/i18n';

export default function AffiliateTrackingSoftware({ seoMeta }) {
  const router = useRouter();
  const t = useTranslations('affiliateTrackingSoftware');

  const metrics = [
    {
      icon: BoltIcon,
      label: t('metrics.trackableActions.label'),
      value: t('metrics.trackableActions.value')
    },
    {
      icon: TicketIcon,
      label: t('metrics.worksWith.label'),
      value: t('metrics.worksWith.value')
    },
    {
      icon: AdjustmentsHorizontalIcon,
      label: t('metrics.attribution.label'),
      value: t('metrics.attribution.value')
    },
    {
      icon: ShieldCheckIcon,
      label: t('metrics.outcome.label'),
      value: t('metrics.outcome.value')
    }
  ];

  const trackingFeatures = [
    {
      icon: LinkIcon,
      title: t('features.linkTracking.title'),
      description: t('features.linkTracking.description')
    },
    {
      icon: TicketIcon,
      title: t('features.codeTracking.title'),
      description: t('features.codeTracking.description')
    },
    {
      icon: FunnelIcon,
      title: t('features.subidBreakdowns.title'),
      description: t('features.subidBreakdowns.description')
    },
    {
      icon: ChartBarIcon,
      title: t('features.analyticsDashboard.title'),
      description: t('features.analyticsDashboard.description')
    },
    {
      icon: DocumentArrowDownIcon,
      title: t('features.exportableReporting.title'),
      description: t('features.exportableReporting.description')
    }
  ];

  const integrationOptions = [
    {
      icon: CodeBracketIcon,
      title: t('integration.cookieScript.title'),
      tag: t('integration.cookieScript.tag'),
      description: t('integration.cookieScript.description'),
      bullets: [
        t('integration.cookieScript.bullet1'),
        t('integration.cookieScript.bullet2'),
        t('integration.cookieScript.bullet3')
      ],
      snippet: "<script async src='https://afivio.com/js/afivio.min.js' data-afivio='your_company_id'></script>"
    },
    {
      icon: Cog6ToothIcon,
      title: t('integration.restApi.title'),
      tag: t('integration.restApi.tag'),
      description: t('integration.restApi.description'),
      bullets: [
        t('integration.restApi.bullet1'),
        t('integration.restApi.bullet2'),
        t('integration.restApi.bullet3')
      ],
      snippet: "Base URL: https://afivio.com/api/v1\nHeader: X-Company-API-Key: {your_api_key}"
    },
    {
      icon: ClipboardDocumentCheckIcon,
      title: t('integration.manualMode.title'),
      tag: t('integration.manualMode.tag'),
      description: t('integration.manualMode.description'),
      bullets: [
        t('integration.manualMode.bullet1'),
        t('integration.manualMode.bullet2'),
        t('integration.manualMode.bullet3')
      ],
      snippet: "No website code required. Track and manage conversions in your dashboard."
    }
  ];

  const faqs = [
    {
      q: t('faq.whatDoesSoftwareDo.q'),
      a: t('faq.whatDoesSoftwareDo.a')
    },
    {
      q: t('faq.trackLinksAndCodes.q'),
      a: t('faq.trackLinksAndCodes.a')
    },
    {
      q: t('faq.subidSupport.q'),
      a: t('faq.subidSupport.a')
    },
    {
      q: t('faq.howIntegrate.q'),
      a: t('faq.howIntegrate.a')
    },
    {
      q: t('faq.dataUsable.q'),
      a: t('faq.dataUsable.a')
    },
    {
      q: t('faq.suitableForSaaS.q'),
      a: t('faq.suitableForSaaS.a')
    }
  ];

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const appJsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Afivio",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url: "https://afivio.com/affiliate-tracking-software",
    description: t('seo.description')
  };

  return (
      <>
        <SEOMeta
            title={seoMeta?.title || t('seo.title')}
            description={seoMeta?.description || t('seo.description')}
            keywords={seoMeta?.keywords || t('seo.keywords')}
            url={seoMeta?.url || "/affiliate-tracking-software"}
        />

        <Head>
          <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(appJsonLd) }}
          />
          <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
          />
        </Head>

        <div className="bg-gradient-to-b from-gray-50 to-white">
          {/* Hero */}
          <section className="pt-28 pb-20 px-6">
            <div className="max-w-7xl mx-auto text-center">
              <motion.h1
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-5xl lg:text-6xl font-bold bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text text-transparent mb-6"
              >
                {t('hero.title')}
              </motion.h1>
              <motion.p
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 }}
                  className="text-xl text-gray-600 max-w-3xl mx-auto mb-10"
              >
                {t('hero.subtitle')}
              </motion.p>

              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button
                    onClick={() => router.push("/signup")}
                    className="bg-gradient-to-r from-blue-600 to-green-500 text-white px-8 py-4 rounded-xl hover:opacity-90 transition-opacity"
                >
                  {t('hero.cta')}
                </Button>
                <a
                    href="/how-it-works"
                    className="inline-flex items-center justify-center px-8 py-4 rounded-xl border border-gray-200 text-gray-800 hover:bg-gray-50 transition-colors"
                >
                  {t('hero.learnMore')} <ArrowRightIcon className="w-5 h-5 ml-2" />
                </a>
              </div>
            </div>
          </section>

          {/* Metrics */}
          <section className="py-14 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {metrics.map((m, idx) => (
                    <motion.div
                        key={m.label}
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.06 }}
                        className="rounded-2xl bg-gray-50 border border-gray-100 p-6"
                    >
                      <m.icon className="w-9 h-9 text-blue-600 mb-3" />
                      <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
                        {m.label}
                      </p>
                      <p className="mt-2 text-lg font-semibold text-gray-900">{m.value}</p>
                    </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Tracking features */}
          <section className="py-24 bg-gray-50">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-14">
                <h2 className="text-4xl font-bold mb-4">{t('features.title')}</h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  {t('features.subtitle')}
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {trackingFeatures.map((f, idx) => (
                    <motion.div
                        key={f.title}
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.07 }}
                        className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100"
                    >
                      <f.icon className="w-11 h-11 text-blue-600 mb-4" />
                      <h3 className="text-xl font-semibold mb-2">{f.title}</h3>
                      <p className="text-gray-600">{f.description}</p>
                    </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Integration options */}
          <section className="py-24 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-14">
                <h2 className="text-4xl font-bold mb-4">{t('integration.title')}</h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  {t('integration.subtitle')}
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8 items-stretch">
                {integrationOptions.map((opt, idx) => (
                    <motion.div
                        key={opt.title}
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.08 }}
                        className="bg-gray-50 border border-gray-100 rounded-2xl p-7 flex flex-col"
                    >
                      <div className="flex items-center justify-between mb-5">
                        <div className="flex items-center">
                          <div className="w-11 h-11 rounded-xl bg-white border border-gray-100 flex items-center justify-center mr-3">
                            <opt.icon className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">{opt.title}</h3>
                            <p className="text-xs text-gray-500">{opt.tag}</p>
                          </div>
                        </div>
                      </div>

                      <p className="text-gray-700 text-sm mb-4">{opt.description}</p>

                      <ul className="space-y-2 text-sm text-gray-700 mb-5">
                        {opt.bullets.map((b) => (
                            <li key={b} className="flex items-start">
                              <CheckCircleIcon className="h-4 w-4 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                              <span>{b}</span>
                            </li>
                        ))}
                      </ul>

                      <div className="mt-auto bg-white border border-gray-100 rounded-xl p-4">
                        <p className="text-xs font-semibold text-gray-500 mb-2">Example</p>
                        <pre className="whitespace-pre-wrap text-xs font-mono text-gray-800">
                      {opt.snippet}
                    </pre>
                      </div>
                    </motion.div>
                ))}
              </div>

              <div className="mt-10 text-center text-sm text-gray-500">
                {t('integration.detailButton1')}{" "}
                <a href="/detailed-overview" className="text-blue-700 hover:underline">
                  {t('integration.detailButton2')}
                </a>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-24 bg-gray-50">
            <div className="max-w-4xl mx-auto px-6">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold mb-4">{t('faq.title')}</h2>
                <p className="text-gray-600">
                  {t('faq.subtitle')}
                </p>
              </div>

              <div className="space-y-4">
                {faqs.map((f, idx) => (
                    <motion.details
                        key={f.q}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.03 }}
                        className="group bg-white border border-gray-100 rounded-2xl p-6"
                    >
                      <summary className="cursor-pointer list-none flex items-center justify-between">
                        <span className="text-lg font-semibold">{f.q}</span>
                        <span className="ml-4 text-gray-400 group-open:rotate-90 transition-transform">›</span>
                      </summary>
                      <p className="mt-4 text-gray-600 leading-relaxed">{f.a}</p>
                    </motion.details>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-24">
            <div className="max-w-5xl mx-auto px-6 text-center">
              <motion.div
                  initial={{ scale: 0.98, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  className="bg-gradient-to-r from-blue-600 to-green-500 p-12 rounded-3xl text-white shadow-xl"
              >
                <h2 className="text-4xl font-bold mb-5">{t('cta.title')}</h2>
                <p className="text-xl mb-8 max-w-2xl mx-auto">
                  {t('cta.subtitle')}
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <Button gray onClick={() => router.push("/signup")}>
                    {t('cta.cta')}
                  </Button>
                  <a
                      href="/pricing"
                      className="inline-flex items-center justify-center px-8 py-3 rounded-xl bg-white/10 border border-white/20 hover:bg-white/15 transition-colors"
                  >
                    {t('cta.viewPricing')} <ArrowRightIcon className="w-5 h-5 ml-2" />
                  </a>
                </div>
              </motion.div>
            </div>
          </section>
          <SeoInternalLinks />
        </div>
      </>
  );
}

export async function getStaticProps(ctx) {
  const { getI18nPropsWithSeo } = await import('@/utils/i18n');
  return getI18nPropsWithSeo(ctx, 'affiliateTrackingSoftware', '/affiliate-tracking-software');
}
